import { ProfilesList } from "@/components/profiles/ProfilesList";
import ErrorDisplay from "@/components/ErrorDisplay";
import { useProfiles } from "@/hooks/useProfiles";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { PageContainer } from "@/components/ui/page-container";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { FilterSheet } from "@/components/ui/filter-sheet";
import { Input } from "@/components/ui/input";
import ViewToggle from "@/components/ViewToggle";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCustomerName } from "@/utils/customerLookup";

export default function ConfigurationsPage() {
  const { profiles, loading, error } = useProfiles();
  const queryClient = useQueryClient();
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["profiles"] }),
      queryClient.invalidateQueries({ queryKey: ["policies"] }),
    ]);
  }, [queryClient]);
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("configurationsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "deployed" | "draft">("all");

  const filteredProfiles = profiles.filter(profile => {
    const q = searchQuery.trim().toLowerCase();
    const tenantIds = [
      ...(profile.customers ?? []),
      ...(profile.customer ? [profile.customer] : []),
    ];
    const matchesSearch =
      q === "" ||
      (profile.name || '').toLowerCase().includes(q) ||
      (profile.description || '').toLowerCase().includes(q) ||
      profile.id.toLowerCase().includes(q) ||
      tenantIds.some((cid) => {
        if (cid.toLowerCase().includes(q)) return true;
        return getCustomerName(cid).toLowerCase().includes(q);
      });

    const matchesStatus = statusFilter === "all" ||
                         (statusFilter === "draft" && profile.isDraft) ||
                         (statusFilter === "deployed" && !profile.isDraft);

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    localStorage.setItem("configurationsViewPreference", view);
  }, [view]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <div className="text-left mb-8">
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-4">
                <div className="h-6 w-2/3 bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse"></div>
                <div className="flex justify-between pt-4">
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <ErrorDisplay error={error} />
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Configuration Profiles" description="Manage device configuration profiles">
        <Button asChild className="w-full sm:w-auto">
          <Link to="/configurations/new">
            <Plus className="mr-2 h-4 w-4" />
            New Configuration Profile
          </Link>
        </Button>
      </PageHeader>

      {/* Filters and Stats */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-row items-center gap-2 sm:gap-4 w-full md:w-auto">
          <div className="relative flex-1 sm:w-72 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by name, ID, or tenant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
          <FilterSheet activeCount={statusFilter !== "all" ? 1 : 0} title="Filter profiles">
            <Select value={statusFilter} onValueChange={(value: "all" | "deployed" | "draft") => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-40">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Profiles</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="draft">Drafts</SelectItem>
              </SelectContent>
            </Select>
          </FilterSheet>
        </div>
        <div className="flex items-center justify-between md:justify-end gap-4 flex-wrap">
          <div className="flex items-center gap-3 md:gap-6 text-sm text-muted-foreground flex-wrap">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{profiles.length}</Badge>
              <span>Total</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{profiles.filter(p => p.isDraft).length}</Badge>
              <span>Drafts</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="default">{profiles.filter(p => !p.isDraft).length}</Badge>
              <span>Deployed</span>
            </div>
          </div>
          <div className="hidden md:block">
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>
      </div>

      <PullToRefresh onRefresh={handleRefresh}>
        <ProfilesList profiles={filteredProfiles} view={view} searchQuery="" />
      </PullToRefresh>
    </PageContainer>
  );
}

